import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { trackCTAClick } from '../lib/analytics';
import QuotaErrorBanner from '../components/QuotaErrorBanner';
import {
  Upload,
  Download,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  FileUp,
  Info,
  Zap,
} from 'lucide-react';

const STLRepair = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [error, setError] = useState('');
  const [quota, setQuota] = useState({ used: 0, limit: 1, remaining: 1 });

  useEffect(() => {
    fetchQuota();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await api.get('/stl/repair/quota');
      setQuota(response.data);
    } catch (err) {
      console.error('Failed to fetch quota');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.stl')) {
      setFile(selectedFile);
      setError('');
      setRepairResult(null);
    } else {
      setError('Please select a valid STL file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/stl/repair/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { repair_id } = response.data;

      // Poll for completion
      setProcessing(true);
      pollRepairStatus(repair_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
  };

  const pollRepairStatus = async (repairId) => {
    const maxAttempts = 60;
    let attempts = 0;

    const poll = setInterval(async () => {
      try {
        const response = await api.get(`/stl/repair/${repairId}`);
        const repair = response.data;

        if (repair.status === 'completed') {
          clearInterval(poll);
          setRepairResult(repair);
          setUploading(false);
          setProcessing(false);
          fetchQuota();
        } else if (repair.status === 'failed') {
          clearInterval(poll);
          setError(repair.error_message || 'Repair failed');
          setUploading(false);
          setProcessing(false);
        }

        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setError('Repair timeout');
          setUploading(false);
          setProcessing(false);
        }
      } catch (err) {
        clearInterval(poll);
        const errorMsg = err.response?.data?.detail || err.message || 'Failed to check repair status';
        setError(`Repair failed: ${errorMsg}`);
        setUploading(false);
        setProcessing(false);
      }
    }, 2000);
  };

  const handleDownload = async () => {
    if (!repairResult) {
      alert('No repair result available');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const downloadUrl = `${backendUrl}/api/stl/repair/${repairResult.id}/download`;
      
      console.log('Downloading from:', downloadUrl);
      
      // Fetch with auth
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      // Get blob
      const blob = await response.blob();
      
      // Force download using blob URL
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = repairResult.original_filename.replace('.stl', '_repaired.stl');
      
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      console.log('Download triggered');
      
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDirectDownload = async () => {
    if (!repairResult) return;
    
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Use XMLHttpRequest for better download control
      const xhr = new XMLHttpRequest();
      xhr.open('GET', `${backendUrl}/api/stl/repair/${repairResult.id}/download`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.responseType = 'blob';
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          const blob = xhr.response;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = repairResult.original_filename.replace('.stl', '_repaired.stl');
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 100);
        } else {
          setError('Download failed - see console');
        }
      };
      
      xhr.onerror = function() {
        setError('Network error during download');
      };
      
      xhr.send();
      
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">STL Repair</h1>
        <p className="text-text-secondary">
          Upload an STL file to automatically fix geometry issues and improve printability
        </p>
      </div>

      {/* Quota */}
      <div className="bg-bg-light p-4 rounded-lg border border-text-muted/20 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-text-secondary">Repairs Used</span>
          <span className="text-white font-semibold">
            {quota.used} / {quota.limit}
          </span>
        </div>
        <div className="mt-2 w-full bg-bg-dark rounded-full h-2">
          <div
            className="bg-accent h-2 rounded-full transition-all"
            style={{ width: `${(quota.used / quota.limit) * 100}%` }}
          />
        </div>
      </div>

      {/* Upload Area */}
      {!repairResult && (
        <div className="bg-bg-light p-8 rounded-lg border border-text-muted/20 mb-6">
          <div className="text-center">
            {!file ? (
              <label className={`block ${quota.remaining === 0 ? '' : 'cursor-pointer'}`}>
                <input
                  type="file"
                  accept=".stl"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={quota.remaining === 0}
                />
                <div className={`border-2 border-dashed rounded-lg p-12 transition-colors ${
                  quota.remaining === 0
                    ? 'border-red-500/30 bg-red-500/[0.03]'
                    : 'border-text-muted/30 hover:border-accent/50'
                }`}>
                  <FileUp className={`w-16 h-16 mx-auto mb-4 ${quota.remaining === 0 ? 'text-red-400/70' : 'text-text-muted'}`} />
                  <p className="text-white font-semibold mb-2">
                    {quota.remaining > 0 ? 'Click to upload STL file' : 'You\'ve reached your free quota'}
                  </p>
                  <p className="text-text-secondary text-sm">
                    {quota.remaining > 0
                      ? 'Maximum file size: 50MB'
                      : 'Subscribe to Pro for faster processing and higher repair quotas.'}
                  </p>
                  {quota.remaining === 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        trackCTAClick('Upgrade to Pro', 'stl-repair-quota-exhausted');
                        navigate('/dashboard/settings?tab=subscription');
                      }}
                      className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/80 text-white rounded-lg text-sm font-bold shadow-md shadow-primary/25 hover:scale-[1.02] transition"
                      data-testid="repair-upgrade-btn"
                    >
                      <Zap className="w-4 h-4" />
                      Subscribe now — get faster processing
                    </button>
                  )}
                </div>
              </label>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-3 text-white">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="font-semibold">{file.name}</span>
                  <span className="text-text-secondary">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <button
                  onClick={handleUpload}
                  disabled={uploading || processing}
                  className="px-6 py-3 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading || processing ? (
                    <span className="flex items-center gap-2">
                      <Loader className="w-5 h-5 animate-spin" />
                      {uploading ? 'Uploading...' : 'Repairing...'}
                    </span>
                  ) : (
                    'Start Repair'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      <div className="mb-6">
        <QuotaErrorBanner error={error} onClose={() => setError('')} source="stl-repair" />
      </div>

      {/* Results */}
      {repairResult && repairResult.ai_explanation && (
        <div className="space-y-6">
          {/* Download Options */}
          <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
            <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm font-semibold mb-2">⚠️ Download Instructions</p>
              <p className="text-text-secondary text-sm">
                Due to browser security, automatic download may not work in preview mode. 
                Please <strong className="text-white">right-click</strong> the link below and select <strong className="text-white">"Save Link As..."</strong> or <strong className="text-white">"Download Linked File"</strong>.
              </p>
            </div>
            
            <button
              onClick={handleDownload}
              className="w-full px-6 py-3 bg-accent hover:bg-accent-hover text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 mb-3"
            >
              <Download className="w-5 h-5" />
              Try Automatic Download
            </button>
            
            <div className="text-center mb-3">
              <span className="text-text-secondary text-sm">or</span>
            </div>
            
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/stl/repair/${repairResult.id}/download?token=${localStorage.getItem('token')}`}
              className="block w-full px-6 py-3 border-2 border-accent text-accent hover:bg-accent hover:text-white rounded-lg font-semibold transition-colors text-center"
              download={repairResult.original_filename.replace('.stl', '_repaired.stl')}
              target="_blank"
              rel="noopener noreferrer"
            >
              📥 Direct Download Link (Click or Right-Click → Save As)
            </a>
            
            <div className="mt-4 p-3 bg-bg-dark rounded border border-text-muted/20">
              <p className="text-text-secondary text-xs mb-2">💡 Alternative: Copy this URL and paste in a new tab</p>
              <input
                type="text"
                value={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/stl/repair/${repairResult.id}/download?token=${localStorage.getItem('token')}`}
                readOnly
                className="w-full px-3 py-2 bg-bg-light text-white text-xs rounded border border-text-muted/30 font-mono"
                onClick={(e) => {
                  e.target.select();
                  navigator.clipboard.writeText(e.target.value);
                  setError('✅ URL copied! Paste in new browser tab to download');
                  setTimeout(() => setError(''), 3000);
                }}
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
            <h2 className="text-xl font-bold text-white mb-4">Repair Summary</h2>
            <p className="text-text-secondary mb-4">{repairResult.ai_explanation.summary}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <h3 className="text-white font-semibold mb-2">Quality Score</h3>
                <div className="text-3xl font-bold text-accent">
                  {repairResult.ai_explanation.quality_score}/100
                </div>
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Processing Time</h3>
                <div className="text-3xl font-bold text-accent">
                  {repairResult.processing_time?.toFixed(1)}s
                </div>
              </div>
            </div>
          </div>

          {/* Issues Fixed */}
          <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
            <h2 className="text-xl font-bold text-white mb-4">Issues Fixed</h2>
            <ul className="space-y-2">
              {repairResult.issues_found.map((issue, idx) => (
                <li key={idx} className="flex items-start gap-2 text-text-secondary">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Recommendations */}
          {repairResult.ai_explanation.recommendations && (
            <div className="bg-bg-light p-6 rounded-lg border border-text-muted/20">
              <h2 className="text-xl font-bold text-white mb-4">Printing Recommendations</h2>
              <ul className="space-y-2">
                {repairResult.ai_explanation.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-text-secondary">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* New Repair Button */}
          <button
            onClick={() => {
              setFile(null);
              setRepairResult(null);
              setError('');
            }}
            className="w-full px-6 py-3 border border-text-muted/30 hover:border-accent/50 text-white rounded-lg font-semibold transition-colors"
          >
            Repair Another File
          </button>
        </div>
      )}
    </div>
  );
};

export default STLRepair;
